import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useStore } from "@/lib/store";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Worksheet } from "@shared/schema";
import { FileText, Upload, Download, Trash2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export default function WorksheetModal() {
  const { t } = useTranslation("worksheet");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const { worksheetModalOpen, setWorksheetModalOpen } = useStore();

  // Fetch worksheets
  const { data: worksheets = [], isLoading } = useQuery<Worksheet[]>({
    queryKey: ["/api/worksheets"],
    queryFn: () => apiRequest("GET", "/api/worksheets"),
    enabled: worksheetModalOpen,
  });

  // Upload worksheet mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch("/api/worksheets", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/worksheets"] });
      toast({
        title: t("successTitle"),
        description: t("uploadSuccess"),
      });
    },
    onError: () => {
      toast({
        title: t("errorTitle"),
        description: t("errorDesc"),
        variant: "destructive",
      });
    },
  });

  // Delete worksheet mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Jak wyzej (VP-326): `.json()` na sparsowanym obiekcie rzucalo TypeError.
      // Backend odpowiada `okBody(...)`, czyli `{ code, message }`.
      return apiRequest<{ code: string; message: string }>("DELETE", `/api/worksheets/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/worksheets"] });
      toast({
        title: t("successTitle"),
        description: t("deleteSuccess"),
      });
    },
    onError: () => {
      toast({
        title: t("errorTitle"),
        description: t("errorDesc"),
        variant: "destructive",
      });
    },
  });

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    // Validate file type
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast({
        title: t("errorTitle"),
        description: t("invalidType"),
        variant: "destructive",
      });
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: t("errorTitle"),
        description: t("tooLarge"),
        variant: "destructive",
      });
      return;
    }

    uploadMutation.mutate(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDownload = (worksheet: Worksheet) => {
    window.open(`/api/worksheets/${worksheet.id}/download`, "_blank");
  };

  const handleDelete = (id: string) => {
    if (window.confirm(t("deleteConfirm"))) {
      deleteMutation.mutate(id);
    }
  };

  const formatFileSize = (bytes: string) => {
    const size = parseInt(bytes);
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${Math.round(size / (1024 * 1024))} MB`;
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes("pdf"))
      return { icon: FileText, color: "text-red-600", bg: "bg-red-100" };
    if (mimeType.includes("word"))
      return { icon: FileText, color: "text-blue-600", bg: "bg-blue-100" };
    return { icon: FileText, color: "text-gray-600", bg: "bg-gray-100" };
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("pl-PL", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Dialog open={worksheetModalOpen} onOpenChange={setWorksheetModalOpen}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-heading-2 font-extrabold text-white">
            {t("title")}
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6">
            {/* Upload Section */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">{t("uploadSection")}</h3>

              <div
                onClick={handleUploadClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`
                  border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
                  ${
                    isDragging
                      ? "border-primary bg-primary/5"
                      : "border-white/[0.1] hover:border-primary/40"
                  }
                  ${uploadMutation.isPending ? "opacity-50 pointer-events-none" : ""}
                `}
              >
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  {uploadMutation.isPending ? (
                    <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full"></div>
                  ) : (
                    <Upload className="w-8 h-8 text-white/60" />
                  )}
                </div>

                <p className="text-white/30 mb-2">
                  {uploadMutation.isPending ? t("uploading") : t("dropHint")}
                </p>
                <p className="text-sm text-white/40">{t("fileHint")}</p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
              />
            </div>

            {/* Saved Worksheets */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">{t("savedSection")}</h3>

              {isLoading ? (
                // VP-208: ręczne `animate-pulse` → wspólny Skeleton (jednolity wygląd).
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full rounded-lg" />
                  ))}
                </div>
              ) : worksheets.length > 0 ? (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {worksheets.map((worksheet: Worksheet) => {
                    const fileInfo = getFileIcon(worksheet.mimeType);
                    return (
                      <div
                        key={worksheet.id}
                        className="flex items-center justify-between p-4 bg-white/[0.04] rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-10 h-10 ${fileInfo.bg} rounded-lg flex items-center justify-center`}
                          >
                            <fileInfo.icon className={`w-5 h-5 ${fileInfo.color}`} />
                          </div>
                          <div>
                            <p className="font-medium text-white truncate max-w-xs">
                              {worksheet.originalName}
                            </p>
                            <p className="text-sm text-white/40">
                              {t("uploadedAt", {
                                date: formatDate(worksheet.uploadedAt),
                                size: formatFileSize(worksheet.size),
                              })}
                            </p>
                          </div>
                        </div>

                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleDownload(worksheet)}
                            className="text-white/60 hover:text-white/30 transition-colors p-2"
                            title={t("download")}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(worksheet.id)}
                            className="text-white/60 hover:text-red-600 transition-colors p-2"
                            title={t("delete")}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-white/40 text-center py-8">{t("noWorksheets")}</div>
              )}
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-white/[0.1] px-6">
            <div className="flex justify-end">
              <Button onClick={() => setWorksheetModalOpen(false)} className="px-6 py-3">
                {t("close")}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
